/**
 * Counts a stat figure up from zero the first time it scrolls into view.
 *
 * The value arrives already formatted for the draft ("1.200 m²", "98,4 %"), so
 * the leading number is parsed out, animated, and re-formatted in the reader's
 * locale with the original suffix put back. Anything that does not start with a
 * number is rendered unchanged, and `prefers-reduced-motion` skips straight to
 * the final figure.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";

export function CountUpStat({
  value,
  de,
  /** Above-the-fold stats count up on mount; the rest wait to be scrolled to. */
  immediate = false,
}: {
  value: string;
  de: boolean;
  immediate?: boolean;
}) {
  const reduce = useReducedMotion();
  // Memoised on `value`: `String.match` hands back a new array on every render,
  // and this object feeds the animation effect's dependencies. When it went in
  // there unmemoised the effect re-ran each frame, resetting `start`, and the
  // figure stuck a single frame into the count instead of reaching its target.
  const parsed = useMemo(() => {
    const m = value.match(/^([\d.,]+)(.*)$/);
    if (!m) return null;
    return {
      target: Number(m[1].replace(/\./g, "").replace(",", ".")),
      suffix: m[2],
      // German decimals are commas, so "98,4" is one decimal place.
      decimals: m[1].includes(",") ? 1 : 0,
    };
  }, [value]);
  const target = parsed?.target ?? 0;

  const [shown, setShown] = useState(reduce ? target : 0);
  const ref = useRef<HTMLSpanElement>(null);
  const scrolledTo = useInView(ref, { once: true, amount: 0.6 });
  const inView = immediate || scrolledTo;

  useEffect(() => {
    if (!parsed) return;
    if (reduce) {
      setShown(target);
      return;
    }
    if (!inView) return;
    const DURATION = 1100;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / DURATION, 1);
      // ease-out so it decelerates into the final figure
      setShown(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduce, target, parsed]);

  if (!parsed) return <span>{value}</span>;

  return (
    <span ref={ref} className="tabular-nums">
      {new Intl.NumberFormat(de ? "de-DE" : "en-GB", {
        minimumFractionDigits: parsed.decimals,
        maximumFractionDigits: parsed.decimals,
      }).format(shown)}
      {parsed.suffix}
    </span>
  );
}
